import { type ReactNode, useMemo } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';

import { DataTable } from '@/components/data-table';
import { useSubscribeDeviceIconRefs } from '@/components/device-icon/use-subscribe-device-icon-refs';
import type { FaultedDevice } from '@/features/equipment-browser/faulted-devices/faulted-devices.types';
import {
  MISSING_MODE_MESSAGE,
  NO_SELECTABLE_DEVICES_MESSAGE,
  RESET_FAULTED_COLUMN_IDS,
  RESET_FAULTED_TABLE_ID,
} from '@/features/equipment-browser/reset-faulted/reset-faulted.constants';
import type {
  ModeValuesByDeviceId,
  ResetFaultedRow,
} from '@/features/equipment-browser/reset-faulted/reset-faulted.types';
import {
  isMissingMode,
  isSelectable,
} from '@/features/equipment-browser/reset-faulted/reset-faulted.utils';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { ResetFaultedFaultReasonCell } from './cells/reset-faulted-fault-reason-cell';
import { ResetFaultedModeCell } from './cells/reset-faulted-mode-cell';
import { ResetFaultedNameCell } from './cells/reset-faulted-name-cell';
import { ResetFaultedTypeCell } from './cells/reset-faulted-type-cell';

interface ResetFaultedTableProps {
  devices: FaultedDevice[];
  onModeChange: (deviceId: string, mode: string) => void;
  onSelectedDeviceIdsChange: (deviceIds: string[]) => void;
  selectedDeviceIds: string[];
  targetModes: ModeValuesByDeviceId;
}

/**
 * Faulted devices with a select column, name, equipment type, fault reason and
 * the mode to run in after reset. Rows arrive sorted by equipment type, then
 * name. Only resettable rows with a resolved mode can be selected; select-all
 * selects just those. Picking a mode in a row enables its checkbox.
 */
export const ResetFaultedTable = ({
  devices,
  onModeChange,
  onSelectedDeviceIdsChange,
  selectedDeviceIds,
  targetModes,
}: ResetFaultedTableProps): ReactNode => {
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({ privilege: PRIVILEGES.writeCommands });

  const deviceIcons = useMemo(() => devices.map((device) => device.summary.deviceIcon), [devices]);
  useSubscribeDeviceIconRefs(deviceIcons);

  const rows = useMemo<ResetFaultedRow[]>(
    () =>
      devices.map((device) => ({ device, targetMode: targetModes[device.summary.referenceId] })),
    [devices, targetModes]
  );

  const selection = useMemo<RowSelectionState>(
    () => Object.fromEntries(selectedDeviceIds.map((deviceId) => [deviceId, true])),
    [selectedDeviceIds]
  );

  const handleSelectionChange = (nextSelection: RowSelectionState) => {
    onSelectedDeviceIdsChange(Object.keys(nextSelection).filter((id) => nextSelection[id]));
  };

  return (
    <DataTable<ResetFaultedRow>
      data={rows}
      getRowId={({ device }) => device.summary.referenceId}
      id={RESET_FAULTED_TABLE_ID}
      width="full"
    >
      <DataTable.SelectColumn<ResetFaultedRow>
        disabledTooltip={({ device }) => {
          if (!device.isResettable) return device.disabledMessage;
          if (isMissingMode(device, targetModes)) return MISSING_MODE_MESSAGE;
          return permissionTooltipText;
        }}
        enableRowSelection={({ device }) =>
          isSelectable(device, targetModes) && !disabledByPermissions
        }
        onChange={handleSelectionChange}
        selectAllDisabledTooltip={permissionTooltipText ?? NO_SELECTABLE_DEVICES_MESSAGE}
        selectAllMode="all"
        value={selection}
      />

      <DataTable.Column<ResetFaultedRow, ResetFaultedRow>
        accessorFn={(row) => row}
        id={RESET_FAULTED_COLUMN_IDS.NAME}
        minWidth={160}
        sortable={false}
        width={220}
      >
        <DataTable.Header>Name</DataTable.Header>
        <DataTable.Cell<ResetFaultedRow, ResetFaultedRow>>
          {({ original }) => <ResetFaultedNameCell device={original.device} />}
        </DataTable.Cell>
      </DataTable.Column>

      <DataTable.Column<ResetFaultedRow, ResetFaultedRow>
        accessorFn={(row) => row}
        id={RESET_FAULTED_COLUMN_IDS.EQUIPMENT_TYPE}
        minWidth={120}
        sortable={false}
        width={160}
      >
        <DataTable.Header>Equipment Type</DataTable.Header>
        <DataTable.Cell<ResetFaultedRow, ResetFaultedRow>>
          {({ original }) => <ResetFaultedTypeCell device={original.device} />}
        </DataTable.Cell>
      </DataTable.Column>

      <DataTable.Column<ResetFaultedRow, ResetFaultedRow>
        accessorFn={(row) => row}
        growFirst
        id={RESET_FAULTED_COLUMN_IDS.FAULT_REASON}
        minWidth={140}
        sortable={false}
        width={200}
      >
        <DataTable.Header>Fault Reason</DataTable.Header>
        <DataTable.Cell<ResetFaultedRow, ResetFaultedRow>>
          {({ original }) => (
            <ResetFaultedFaultReasonCell faultReasonLabel={original.device.faultReasonLabel} />
          )}
        </DataTable.Cell>
      </DataTable.Column>

      <DataTable.Column<ResetFaultedRow, ResetFaultedRow>
        accessorFn={(row) => row}
        id={RESET_FAULTED_COLUMN_IDS.MODE}
        minWidth={180}
        resizable={false}
        sortable={false}
        width={200}
      >
        <DataTable.Header>Mode</DataTable.Header>
        <DataTable.Cell<ResetFaultedRow, ResetFaultedRow>>
          {({ original }) => (
            <ResetFaultedModeCell
              device={original.device}
              onSelect={onModeChange}
              value={original.targetMode}
            />
          )}
        </DataTable.Cell>
      </DataTable.Column>

      <DataTable.Settings>
        <DataTable.Density value="wide" />
        <DataTable.StickyHeader />
        <DataTable.Styles
          row={{
            base: 'bg-ckds-neutral-900',
            selected: 'bg-ckds-neutral-800',
          }}
        />
      </DataTable.Settings>
    </DataTable>
  );
};
